import { forwardRef } from 'react'
import cx from 'classnames'

import Loader from 'common/components/loaders/Loader'

import {
  fontSizePerSize,
  getColorClassesByVariant,
  heightPerSize,
  paddingPerSize,
} from './Button.styles'
import IconWrapper from './IconWrapper'
import { ButtonProps } from './interfaces'

// forwardRef is needed when Button is used for menuButton prop on MenuButton component
const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant,
    tone,
    loading,
    'data-cy': dataCy,
    'data-testid': dataTestId,
    disabled,
    prefixIcon,
    suffixIcon,
    type = 'button',
    size = 'medium',
    isShrinkable = false,
    children,
    ...otherProps
  },
  ref
) {
  const hasPadding = variant !== 'icon' && variant !== 'link'
  const hasChildren = !!children

  const colorStyles = variant
    ? getColorClassesByVariant(variant, tone)
    : undefined
  const fontSize = fontSizePerSize[size] ?? fontSizePerSize['medium']
  const height = heightPerSize[size] ?? heightPerSize['medium']
  const padding = paddingPerSize[size] ?? paddingPerSize['medium']

  const baseStyles = colorStyles?.base ?? {}
  const hoverStyles = !disabled && !loading ? (colorStyles?.hover ?? {}) : {}

  const buttonClassnames = cx(
    'uppercase inline-flex rounded-full justify-center items-center focus:outline-none whitespace-nowrap font-medium relative transform-gpu',
    fontSize,
    baseStyles.background,
    baseStyles.text,
    baseStyles.border,
    baseStyles.outline,
    hoverStyles.background,
    hoverStyles.text,
    hoverStyles.border,
    {
      'min-w-max': !isShrinkable,
      'w-full': isShrinkable,
      ...(padding && { [padding]: hasPadding }),
      'opacity-50 cursor-not-allowed': disabled || loading,
    }
  )

  return (
    <button
      ref={ref}
      data-cy={dataCy}
      data-testid={dataTestId}
      className={cx(buttonClassnames, className)}
      style={hasPadding ? { height } : undefined}
      disabled={disabled || loading}
      type={type}
      {...otherProps}
    >
      {loading || prefixIcon ? (
        <>
          <span className="inline-block">
            {loading ? (
              <span className="absolute inset-0">
                <Loader isGrayscale size="small" />
              </span>
            ) : (
              <IconWrapper hasMargin={hasChildren}>{prefixIcon}</IconWrapper>
            )}
          </span>

          {loading ? (
            <span aria-hidden={loading} className={cx({ invisible: loading })}>
              {children}
            </span>
          ) : (
            children
          )}
        </>
      ) : (
        <>
          {!loading && prefixIcon ? (
            <IconWrapper hasMargin={hasChildren}>{prefixIcon}</IconWrapper>
          ) : undefined}
          {loading ? (
            <span className="absolute inset-0">
              <Loader isGrayscale size="small" />
            </span>
          ) : undefined}
          {loading ? (
            <span aria-hidden={loading} className={cx({ invisible: loading })}>
              {children}
            </span>
          ) : (
            children
          )}

          {!loading && suffixIcon && (
            <IconWrapper hasMargin={false} isSuffix={true}>
              {suffixIcon}
            </IconWrapper>
          )}
        </>
      )}
    </button>
  )
})

export default Button
